/**
 * Path-based query key helpers.
 *
 * Derives stable, hierarchical TanStack Query keys from REST paths so that:
 *   1. Callers don't have to maintain parallel `queryKey` arrays.
 *   2. Mutations can auto-invalidate all queries under a path prefix
 *      (drop the entity id to get the collection).
 *
 * Canonical scheme: full path segments, as-is. Example:
 *   `/core/activities/crm/lead/01ABC...` → `["core", "activities", "crm", "lead", "01ABC..."]`
 *
 * Params (pagination/filters) are appended as the final segment when present.
 */

export type QueryKey = readonly unknown[];

// Crockford base32 ULID — 26 chars, case-insensitive.
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;
const NUMERIC_ID_RE = /^\d+$/;

/** Whether a path segment looks like an entity id (ULID or numeric). */
export function isIdSegment(segment: string): boolean {
  return ULID_RE.test(segment) || NUMERIC_ID_RE.test(segment);
}

/** Split `/a/b/c?x=1` → `["a", "b", "c"]`. Tolerates leading/trailing slashes and query strings. */
export function splitPath(path: string): string[] {
  const withoutQuery = path.split("?", 1)[0] ?? "";
  return withoutQuery.split("/").filter(Boolean);
}

/**
 * Derive a query key from an API path + optional request params.
 *
 * @example
 *   pathToQueryKey("/core/users") // ["core", "users"]
 *   pathToQueryKey("/core/users", { page: 1 }) // ["core", "users", { page: 1 }]
 *   pathToQueryKey("/car-rental/bookings/01ABC/timeline")
 *   // ["car-rental", "bookings", "01ABC", "timeline"]
 */
export function pathToQueryKey(
  path: string,
  params?: Record<string, unknown>,
): QueryKey {
  const segments = splitPath(path);
  const hasParams = params !== undefined && Object.keys(params).length > 0;
  return hasParams ? [...segments, params] : segments;
}

/**
 * Derive the invalidation prefix for a path — i.e. the collection key,
 * stopping at the first segment that looks like an id.
 *
 * @example
 *   pathToInvalidationPrefix("/core/users") // ["core", "users"]
 *   pathToInvalidationPrefix("/core/users/01ABC") // ["core", "users"]
 *   pathToInvalidationPrefix("/car-rental/bookings/01ABC/timeline")
 *   // ["car-rental", "bookings"]
 *   pathToInvalidationPrefix("/core/activities/crm/lead/01ABC")
 *   // ["core", "activities", "crm", "lead"]
 */
export function pathToInvalidationPrefix(path: string): QueryKey {
  const segments = splitPath(path);
  const prefix: string[] = [];
  for (const seg of segments) {
    if (isIdSegment(seg)) break;
    prefix.push(seg);
  }
  return prefix;
}
